import React from 'react';
import type { CSSProperties } from 'react';
import { isEditorAddress } from '../../editor/editorVisit';

// What an element with nothing to draw shows. A merchant arranging a page must still see where the
// element will sit, so the builder gets a named outline; a shopper gets nothing at all, because a shop
// never advertises a hole in itself.

type PlaceholderProps = {
  /** The element's own name, so the outline says which node the merchant is looking at. */
  label: string;
  /** Why it is empty, in the merchant's terms — "no product on this page", not "undefined". */
  hint?: string;
  className?: string;
  style?: CSSProperties;
};

/** Deliberately plain, and never a colour or a font a theme owns: this is scaffolding, not design. */
const OUTLINE: CSSProperties = {
  display: 'block',
  padding: '10px 12px',
  border: '1px dashed currentColor',
  borderRadius: 4,
  opacity: 0.45,
  fontSize: 13,
  lineHeight: 1.4
};

// Reports whether this page is being arranged in the builder rather than shopped.
export const isBuilderPage = (): boolean => isEditorAddress();

// Draws an element's empty state: a named outline in the builder, and nothing to a shopper.
export const ElementPlaceholder: React.FC<PlaceholderProps> = ({ label, hint, className, style }) => {
  if (!isBuilderPage()) return null;
  return (
    <span className={className ? `xe-placeholder ${className}` : 'xe-placeholder'} style={{ ...OUTLINE, ...style }}>
      {label}
      {hint ? <em style={{ display: 'block', fontStyle: 'normal', opacity: 0.75 }}>{hint}</em> : null}
    </span>
  );
};
